import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import PublicSiteLayout from '../../layouts/PublicSiteLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiBaseUrl } from '../../services/httpClient';
import {
  CALL_TIME_PRESETS,
  PURPOSE_PRESETS,
  PUBLIC_LANGUAGES,
  callTimeOptions,
  purposeOptions,
  tPublic,
  translateApiMessage,
} from '../../i18n/publicSite';
import './PublicEnquiryForm.css';

const API_BASE_URL = getApiBaseUrl();
const CALL_NUMBERS = ['9885750476', '7815970729'];

const STEPS = ['purpose', 'name', 'phone', 'otp', 'details', 'done'];

const PublicEnquiryForm = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key, vars) => tPublic(language, key, vars);

  const [step, setStep] = useState('purpose');
  const [purpose, setPurpose] = useState('');
  const [customPurpose, setCustomPurpose] = useState('');
  const [personName, setPersonName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [village, setVillage] = useState('');
  const [address, setAddress] = useState('');
  const [preferredCallTime, setPreferredCallTime] = useState('anytime');
  const [remarks, setRemarks] = useState('');
  const [callbackConsent, setCallbackConsent] = useState(false);
  const [enquiryId, setEnquiryId] = useState('');
  const [otpSentOk, setOtpSentOk] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef(null);
  const continueBtnRef = useRef(null);

  const purposes = useMemo(() => purposeOptions(language), [language]);
  const callTimes = useMemo(() => callTimeOptions(language), [language]);

  const stepIndex = Math.max(0, STEPS.indexOf(step));
  const progress = step === 'done' ? 100 : Math.round((stepIndex / (STEPS.length - 1)) * 100);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    setError('');
    setInfo('');
    const timeout = setTimeout(() => {
      if (step === 'purpose' && purpose && purpose !== 'other') {
        continueBtnRef.current?.focus?.();
        return;
      }
      inputRef.current?.focus?.();
    }, 180);
    return () => clearTimeout(timeout);
  }, [step]);

  useEffect(() => {
    document.title = t('pageTitle');
    setError('');
    if (step === 'otp' && phone) {
      setInfo(t('otpSentTo', { phone }));
    } else {
      setInfo('');
    }
  }, [language]);

  const englishPurposeLabel = useMemo(() => {
    const preset = PURPOSE_PRESETS.find((opt) => opt.id === purpose);
    if (purpose === 'other') return customPurpose.trim() || preset?.enLabel || 'Other enquiry';
    return preset?.enLabel || '';
  }, [purpose, customPurpose]);

  const workPurposeLabel = useMemo(() => {
    if (purpose === 'other') {
      return customPurpose.trim() || t('purposeOther');
    }
    const match = purposes.find((opt) => opt.id === purpose);
    return match?.label || '';
  }, [purpose, customPurpose, purposes, language]);

  const englishCallTime = useMemo(
    () => CALL_TIME_PRESETS.find((opt) => opt.id === preferredCallTime)?.enLabel || 'Anytime',
    [preferredCallTime],
  );

  const selectPurpose = (id) => {
    setPurpose(id);
    setError('');
    if (id !== 'other') {
      requestAnimationFrame(() => {
        continueBtnRef.current?.focus?.();
        continueBtnRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  const goNextFromPurpose = () => {
    if (!purpose) {
      setError(t('errChoosePurpose'));
      return;
    }
    if (purpose === 'other' && !customPurpose.trim()) {
      setError(t('errDescribe'));
      return;
    }
    setStep('name');
  };

  const goNextFromName = () => {
    if (!personName.trim() || personName.trim().length < 2) {
      setError(t('errName'));
      return;
    }
    setStep('phone');
  };

  const sendOtp = async () => {
    const phone10 = phone.replace(/\D/g, '').slice(0, 10);
    if (phone10.length !== 10) {
      setError(t('errPhone'));
      return;
    }
    if (!callbackConsent) {
      setError(t('errConsent'));
      return;
    }
    try {
      setBusy(true);
      setError('');
      const { data } = await axios.post(
        `${API_BASE_URL}/enquiries/public/send-otp`,
        {
          phone: phone10,
          person_name: personName.trim(),
          work_purpose: englishPurposeLabel,
          custom_purpose: customPurpose.trim(),
          callback_consent: true,
          enquiry_id: enquiryId || undefined,
        },
        { timeout: 25000 },
      );
      if (!data?.success && !data?.leadSaved) {
        throw new Error(data?.message || t('errSendOtp'));
      }

      setPhone(phone10);
      if (data?.enquiry_id) setEnquiryId(data.enquiry_id);
      setCooldown(Number(data.cooldownSeconds) || 30);

      // Always open OTP enter/validate step (even if SMS failed — user can Resend).
      setOtpSentOk(Boolean(data?.otpSent));
      if (data?.otpSent) {
        setInfo(t('otpSentTo', { phone: phone10 }));
      } else {
        setInfo(translateApiMessage(language, data?.message) || t('otpNotDelivered'));
      }
      setStep('otp');
    } catch (err) {
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.message || err.message;
      const leadId = err?.response?.data?.enquiry_id;
      if (leadId) setEnquiryId(leadId);

      // Lead may already be saved — still show OTP step so user can resend / skip.
      if (status === 504 || status === 502 || /504|timeout/i.test(String(apiMsg))) {
        setOtpSentOk(false);
        setInfo(t('otpNotDelivered'));
        setStep('otp');
        setError('');
      } else if (err?.response?.data?.leadSaved) {
        setOtpSentOk(Boolean(err?.response?.data?.otpSent));
        setInfo(translateApiMessage(language, apiMsg) || t('otpNotDelivered'));
        setStep('otp');
        setError('');
      } else {
        setError(translateApiMessage(language, apiMsg) || t('errSendOtp'));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitEnquiry = async ({ skipOtp = false } = {}) => {
    if (!skipOtp && otpSentOk && !/^\d{6}$/.test(otp.trim())) {
      setError(t('errOtp'));
      return;
    }
    if (!callbackConsent) {
      setError(t('errConsent'));
      return;
    }
    try {
      setBusy(true);
      setError('');
      const { data } = await axios.post(
        `${API_BASE_URL}/enquiries/public`,
        {
          person_name: personName.trim(),
          phone,
          otp: skipOtp || !otpSentOk ? undefined : otp.trim(),
          allow_unverified: skipOtp || !otpSentOk,
          work_purpose: englishPurposeLabel,
          custom_purpose: customPurpose.trim(),
          village: village.trim(),
          address: address.trim(),
          preferred_call_time: englishCallTime,
          remarks: remarks.trim(),
          callback_consent: true,
          enquiry_id: enquiryId || undefined,
        },
        { timeout: 20000 },
      );
      if (!data?.success) {
        throw new Error(data?.message || t('errSubmit'));
      }
      if (data?.data?.id) setEnquiryId(data.data.id);
      setStep('done');
    } catch (err) {
      const message =
        translateApiMessage(language, err?.response?.data?.message || err.message) || t('errSubmit');
      // Even if final update fails, lead was already captured at phone step.
      if (enquiryId || /504|502|timeout/i.test(err?.message || '')) {
        setInfo(t('leadSavedCall'));
        setStep('done');
        setError('');
      } else {
        setError(message);
        if (/otp/i.test(err?.response?.data?.message || err.message || '')) {
          setStep('otp');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setStep('purpose');
    setPurpose('');
    setCustomPurpose('');
    setPersonName('');
    setPhone('');
    setOtp('');
    setVillage('');
    setAddress('');
    setPreferredCallTime('anytime');
    setRemarks('');
    setCallbackConsent(false);
    setEnquiryId('');
    setOtpSentOk(true);
    setError('');
    setInfo('');
    setCooldown(0);
  };

  const firstName = personName.trim().split(' ')[0];

  return (
    <PublicSiteLayout>
      <div className={`pef-page pef-lang-${language}`}>
        <div className="pef-hero" aria-hidden="true" />
        <div className="pef-shell">
          <header className="pef-brand">
            <p className="pef-kicker">{t('kicker')}</p>
            <h1>{t('title')}</h1>
            <p className="pef-lead">{t('lead')}</p>
            <div className="pef-langs" role="group" aria-label={t('changeLanguage')}>
              {PUBLIC_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className={`pef-lang-btn ${language === lang.code ? 'is-selected' : ''}`}
                  onClick={() => changeLanguage(lang.code)}
                  lang={lang.code}
                >
                  {lang.nativeName}
                </button>
              ))}
            </div>
          </header>

          <aside className="pef-callbar" aria-label={t('callNow')}>
            <span className="pef-callbar-label">{t('callNow')}</span>
            <div className="pef-callbar-actions">
              {CALL_NUMBERS.map((num) => (
                <a key={num} className="pef-call-btn" href={`tel:+91${num}`}>
                  {num}
                </a>
              ))}
              <a
                className="pef-wa-btn"
                href={`https://wa.me/91${CALL_NUMBERS[0]}?text=${encodeURIComponent(t('waPrefill'))}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('whatsapp')}
              </a>
            </div>
          </aside>

          {step !== 'done' && (
            <div className="pef-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
              <div className="pef-progress-bar" style={{ width: `${progress}%` }} />
            </div>
          )}

          <section className="pef-card" aria-live="polite">
            {error && <div className="pef-alert pef-alert-error">{error}</div>}
            {info && !error && <div className="pef-alert pef-alert-info">{info}</div>}

            {step === 'purpose' && (
              <div className="pef-step">
                <h2>{t('purposeTitle')}</h2>
                <p className="pef-sub">{t('purposeSub')}</p>
                <div className="pef-purpose-grid">
                  {purposes.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`pef-purpose ${purpose === opt.id ? 'is-selected' : ''}`}
                      onClick={() => selectPurpose(opt.id)}
                    >
                      <span className="pef-purpose-text">
                        <strong>{opt.label}</strong>
                        <small>{opt.hint}</small>
                      </span>
                    </button>
                  ))}
                </div>
                {purpose === 'other' && (
                  <label className="pef-field">
                    <span>{t('describeBriefly')}</span>
                    <input
                      ref={inputRef}
                      type="text"
                      value={customPurpose}
                      onChange={(e) => setCustomPurpose(e.target.value)}
                      placeholder={t('describePlaceholder')}
                      maxLength={200}
                    />
                  </label>
                )}
                <button
                  ref={continueBtnRef}
                  type="button"
                  className="pef-primary"
                  onClick={goNextFromPurpose}
                >
                  {t('continue')}
                </button>
              </div>
            )}

            {step === 'name' && (
              <div className="pef-step">
                <button type="button" className="pef-back" onClick={() => setStep('purpose')}>
                  {t('back')}
                </button>
                <h2>{t('nameTitle')}</h2>
                <p className="pef-sub">{t('nameSub')}</p>
                <label className="pef-field">
                  <span>{t('fullName')}</span>
                  <input
                    ref={inputRef}
                    type="text"
                    value={personName}
                    onChange={(e) => setPersonName(e.target.value)}
                    placeholder={t('namePlaceholder')}
                    autoComplete="name"
                    maxLength={120}
                    onKeyDown={(e) => e.key === 'Enter' && goNextFromName()}
                  />
                </label>
                <button type="button" className="pef-primary" onClick={goNextFromName}>
                  {t('continue')}
                </button>
              </div>
            )}

            {step === 'phone' && (
              <div className="pef-step">
                <button type="button" className="pef-back" onClick={() => setStep('name')}>
                  {t('back')}
                </button>
                <h2>{t('phoneTitle')}</h2>
                <p className="pef-sub">{t('phoneSub')}</p>
                <label className="pef-field">
                  <span>{t('phoneLabel')}</span>
                  <div className="pef-phone-row">
                    <span className="pef-prefix">+91</span>
                    <input
                      ref={inputRef}
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="9876543210"
                      autoComplete="tel"
                      onKeyDown={(e) => e.key === 'Enter' && !busy && sendOtp()}
                    />
                  </div>
                </label>
                {workPurposeLabel ? <p className="pef-chip-summary">{workPurposeLabel}</p> : null}

                <label className="pef-consent">
                  <input
                    type="checkbox"
                    checked={callbackConsent}
                    onChange={(e) => {
                      setCallbackConsent(e.target.checked);
                      setError('');
                    }}
                  />
                  <span>{t('callbackConsent')}</span>
                </label>

                <button
                  type="button"
                  className="pef-primary"
                  disabled={busy || !callbackConsent}
                  onClick={sendOtp}
                >
                  {busy ? t('sendingOtp') : t('sendOtp')}
                </button>
              </div>
            )}

            {step === 'otp' && (
              <div className="pef-step">
                <button type="button" className="pef-back" onClick={() => setStep('phone')}>
                  {t('back')}
                </button>
                <h2>{t('otpTitle')}</h2>
                <p className="pef-sub">
                  {otpSentOk ? t('otpSub', { phone }) : t('otpSubRetry', { phone })}
                </p>
                <label className="pef-field">
                  <span>{t('otpLabel')}</span>
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="pef-otp-input"
                    autoComplete="one-time-code"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && otp.length === 6) {
                        setOtpSentOk(true);
                        setStep('details');
                      }
                    }}
                  />
                </label>
                <div className="pef-otp-actions">
                  <button
                    type="button"
                    className="pef-linkish"
                    disabled={busy || cooldown > 0}
                    onClick={sendOtp}
                  >
                    {busy && step === 'otp'
                      ? t('sendingOtp')
                      : cooldown > 0
                        ? t('resendIn', { seconds: cooldown })
                        : t('resendOtp')}
                  </button>
                </div>
                <button
                  type="button"
                  className="pef-primary"
                  disabled={otp.length !== 6}
                  onClick={() => {
                    if (!/^\d{6}$/.test(otp)) {
                      setError(t('errOtp'));
                      return;
                    }
                    setOtpSentOk(true);
                    setStep('details');
                  }}
                >
                  {t('verifyContinue')}
                </button>
                <button
                  type="button"
                  className="pef-linkish pef-skip-otp"
                  disabled={busy}
                  onClick={() => {
                    setOtpSentOk(false);
                    setStep('details');
                  }}
                >
                  {t('skipOtpContinue')}
                </button>
              </div>
            )}

            {step === 'details' && (
              <div className="pef-step">
                <button
                  type="button"
                  className="pef-back"
                  onClick={() => setStep(otpSentOk ? 'otp' : 'phone')}
                >
                  {t('back')}
                </button>
                <h2>{t('detailsTitle')}</h2>
                <p className="pef-sub">{t('detailsSub')}</p>

                <label className="pef-field">
                  <span>{t('villageLabel')}</span>
                  <input
                    ref={inputRef}
                    type="text"
                    value={village}
                    onChange={(e) => setVillage(e.target.value)}
                    placeholder={t('villagePlaceholder')}
                    maxLength={120}
                  />
                </label>

                <label className="pef-field">
                  <span>{t('addressLabel')}</span>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t('addressPlaceholder')}
                    maxLength={200}
                  />
                </label>

                <fieldset className="pef-fieldset">
                  <legend>{t('callTimeLegend')}</legend>
                  <div className="pef-chips">
                    {callTimes.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`pef-chip ${preferredCallTime === opt.id ? 'is-selected' : ''}`}
                        onClick={() => setPreferredCallTime(opt.id)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="pef-field">
                  <span>{t('remarksLabel')}</span>
                  <textarea
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder={t('remarksPlaceholder')}
                    maxLength={400}
                  />
                </label>

                <div className="pef-summary">
                  <div><strong>{t('summaryName')}</strong> {personName}</div>
                  <div><strong>{t('summaryPhone')}</strong> +91 {phone}</div>
                  <div><strong>{t('summaryNeed')}</strong> {workPurposeLabel}</div>
                </div>

                <p className="pef-consent-note">{t('callbackConsentNote')}</p>

                <button
                  type="button"
                  className="pef-primary"
                  disabled={busy}
                  onClick={() => submitEnquiry({ skipOtp: !otpSentOk })}
                >
                  {busy ? t('submitting') : t('submitEnquiry')}
                </button>
              </div>
            )}

            {step === 'done' && (
              <div className="pef-step pef-done">
                <div className="pef-done-mark" aria-hidden="true">✓</div>
                <h2>{t('doneTitle')}</h2>
                <p className="pef-sub">
                  {firstName
                    ? t('thanksNamed', { name: firstName, need: workPurposeLabel })
                    : t('thanksAnon', { need: workPurposeLabel })}
                </p>
                <div className="pef-done-calls">
                  <p>{t('callDirect')}</p>
                  {CALL_NUMBERS.map((num) => (
                    <a key={num} className="pef-call-btn pef-call-btn-lg" href={`tel:+91${num}`}>
                      {t('callNumber', { num })}
                    </a>
                  ))}
                </div>
                <button type="button" className="pef-secondary" onClick={resetForm}>
                  {t('anotherEnquiry')}
                </button>
              </div>
            )}
          </section>

          <p className="pef-footnote">{t('footnote')}</p>
        </div>
      </div>
    </PublicSiteLayout>
  );
};

export default PublicEnquiryForm;
